import { defineRule, useForm } from 'vee-validate'
import { usePersonalAccountFormState } from '../../../../utilities/account/useAccountFormPersonal'
import { onMounted, onUnmounted } from 'vue'
import {
    ComplianceProfiles,
    EcspComplianceProfile,
    EsacComplianceProfile,
    usePersonalAccountDataState,
} from '../../../../utilities/account/useAccountDataPersonal'
import { getPlatformSettings } from '../../../../utilities/usePlatformSettings'
import { useLegalAccountDataState } from '../../../../utilities/account/useAccountDataLegal'
import { isLegalComplianceProfileCategoryComplete } from '../legal/form/forms'
import { esacPersonalComplianceProfileConfig } from '../../../account/personal/complianceProfile/esacComplianceProfile'
import { esacLegalComplianceProfileConfig } from '../../../account/legal/complianceProfile/esacComplianceProfile'

export function initializeNaturalWidgetForm(
    validationSchema: any,
    initialValues = null,
    complianceProfile: keyof ComplianceProfiles = 'esac'
) {
    const { naturalProfile: account } = usePersonalAccountDataState()

    let initialComplianceProfile = null
    if (complianceProfile === 'esac') {
        initialComplianceProfile = account.compliance_profiles.esac
    } else {
        initialComplianceProfile = account.compliance_profiles.ecsp
    }

    const form = useForm({
        validationSchema: validationSchema,
        initialValues: initialValues ?? initialComplianceProfile,
    })
    const formState = usePersonalAccountFormState()

    onMounted(() => {
        // @ts-ignore
        formState.currentForm = form
    })
    onUnmounted(() => {
        formState.currentForm = null
    })
}

export async function isNaturalComplianceProfileCategoryComplete(
    key: keyof EsacComplianceProfile | keyof EcspComplianceProfile,
    denyKey: string = null
) {
    const currentAccountValidationSchema = getNaturalWidgetFormValidationRules(
        key,
        getPlatformSettings().compliance_profile,
        denyKey
    )
    const { naturalProfile: account } = usePersonalAccountDataState()

    const form = useForm({
        validationSchema: currentAccountValidationSchema,
        initialValues: account.compliance_profiles[getPlatformSettings().compliance_profile],
        validateOnMount: true,
    })

    const { valid } = await form.validate()
    return valid
}

export async function isEsacLegalComplianceProfileComplete() {
    let isLegalWizardCompleted = true

    if (useLegalAccountDataState().legalProfile) {
        for (const key in esacLegalComplianceProfileConfig) {
            if (
                !(await isLegalComplianceProfileCategoryComplete(esacLegalComplianceProfileConfig[key].validationKey))
            ) {
                isLegalWizardCompleted = false
            }
        }
    } else {
        isLegalWizardCompleted = false
    }

    return isLegalWizardCompleted
}

export async function isEsacNaturalComplianceProfileComplete() {
    let isNaturalWizardCompleted = true
    for (const key in esacPersonalComplianceProfileConfig) {
        if (
            !(await isNaturalComplianceProfileCategoryComplete(esacPersonalComplianceProfileConfig[key].validationKey))
        ) {
            isNaturalWizardCompleted = false
        }
    }
    return isNaturalWizardCompleted
}

export function clearFormValuesNoAnswer(formValues) {
    if (formValues.value.no_answer === true) {
        for (const key in formValues.value) {
            if (key !== 'no_answer') {
                formValues.value[key] = null
            }
        }
    }
}

defineRule('multiselect-required', (value) => {
    let isValid = false
    if (value) {
        Object.keys(value).forEach((key) => {
            if (value[key] === true || (value[key]?.length && value[key].length > 0)) {
                isValid = true
            }
        })
    }
    return isValid
})

export function getNaturalWidgetFormValidationRules(
    key: keyof EsacComplianceProfile | keyof EcspComplianceProfile,
    complianceProfile: keyof ComplianceProfiles = 'esac',
    denyKey: string = null
) {
    let category = null
    if (complianceProfile === 'esac') {
        category = new EsacComplianceProfile()[key]
    } else {
        category = new EcspComplianceProfile()[key]
    }

    const requiredFields = getPlatformSettings().compliance_profile_required_fields[key]
    const validationRules = {}
    Object.keys(category).forEach((categoryKey) => {
        if (requiredFields && requiredFields.includes(categoryKey)) {
            if (denyKey && denyKey.startsWith(key + '.' + categoryKey)) {
                validationRules[key + '.' + categoryKey] = 'required'
            } else if (
                category[categoryKey] === null ||
                category[categoryKey] === '' ||
                category[categoryKey] === false
            ) {
                validationRules[key + '.' + categoryKey] = denyKey ? 'required_unless:' + denyKey : 'required'
            } else {
                validationRules[key + '.' + categoryKey] = 'multiselect-required'
            }
        }
    })

    return validationRules
}

export function isRequiredNaturalComplianceProfileAttribute(
    category: keyof EsacComplianceProfile | keyof EcspComplianceProfile,
    key: string
) {
    return getPlatformSettings().compliance_profile_required_fields[category]?.includes(key)
}

export type OwnershipType = 'control_by_share' | 'control_by_other_means' | 'official_senior_manager'

export const ownershipTypes: { key: OwnershipType; label: string }[] = [
    {
        key: 'control_by_share',
        label: 'Control by share of ownership',
    },
    {
        key: 'control_by_other_means',
        label: 'Control by other means',
    },
    {
        key: 'official_senior_manager',
        label: 'Official senior manager',
    },
]
